import { useState, useEffect, useCallback } from 'react'
import { TrendingUp, MessageSquare, Clock, Users, Download, AlertCircle } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { API_BASE_URL } from '@/config'
import { auth } from '@/firebase'
import { useAuth } from '@/hooks/useAuth'

// Matches the server's AnalyticsService.getProjectAnalytics response
interface ProjectAnalytics {
  totalSessions: number
  totalMessages: number
  avgSessionDuration: number
  avgResponseTime: number
  satisfactionScore: number
  topCategories: Array<{ category: string; count: number }>
  dailyStats: Array<{ date: string; sessions: number; messages: number }>
  period: { startDate: string; endDate: string }
}

// Matches the server's analytics/topics endpoint response
interface TopicsResponse {
  topics: Array<{ topic: string; count: number; trend: 'up' | 'stable' | 'down' }>
  period: { days: number }
}

interface AnalyticsData {
  analytics: ProjectAnalytics | null
  topics: TopicsResponse | null
}

export default function Analytics() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [data, setData] = useState<AnalyticsData>({ analytics: null, topics: null })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dateRange, setDateRange] = useState('7d')
  const [projectId, setProjectId] = useState<string | null>(null)
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([])

  const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90

  // Fetch projects list
  useEffect(() => {
    async function fetchProjects() {
      try {
        const token = await auth.currentUser?.getIdToken()
        if (!token) return
        const res = await fetch(`${API_BASE_URL}/projects`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (res.ok) {
          const body = await res.json()
          const list = (body.projects || []).map((p: any) => ({ id: p.id, name: p.name }))
          setProjects(list)
          if (list.length > 0 && !projectId) {
            setProjectId(list[0].id)
          }
        }
      } catch {
        // handled by analytics fetch
      }
    }
    fetchProjects()
  }, [])

  // Fetch analytics when project or date range changes
  const fetchAnalytics = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    setError(null)
    try {
      const token = await auth.currentUser?.getIdToken()
      if (!token) {
        setError('Not authenticated')
        setLoading(false)
        return
      }

      const [analyticsRes, topicsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/analytics/${projectId}?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/analytics/${projectId}/topics?days=${days}&limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ])

      let analytics: ProjectAnalytics | null = null
      let topics: TopicsResponse | null = null

      if (analyticsRes.ok) {
        analytics = await analyticsRes.json()
      }
      if (topicsRes.ok) {
        topics = await topicsRes.json()
      }

      if (!analytics) {
        setError('Failed to load analytics data')
      }

      setData({ analytics, topics })
    } catch (err) {
      console.error('Failed to fetch analytics:', err)
      setError('Failed to load analytics data')
    } finally {
      setLoading(false)
    }
  }, [projectId, days])

  useEffect(() => {
    fetchAnalytics()
  }, [fetchAnalytics])

  const handleExport = async () => {
    if (!projectId) return
    try {
      const token = await auth.currentUser?.getIdToken()
      if (!token) return
      const res = await fetch(`${API_BASE_URL}/analytics/${projectId}/export?days=${days}&format=json`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const exportData = await res.json()
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `analytics-${projectId}-${days}d.json`
        a.click()
        URL.revokeObjectURL(url)
      }
    } catch (err) {
      console.error('Export failed:', err)
    }
  }

  const analytics = data.analytics
  const dailyStats = analytics?.dailyStats || []
  const topics = data.topics?.topics || []
  const maxSessions = Math.max(...(dailyStats.map(d => d.sessions)), 1)

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('analytics.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('analytics.subtitle')}</p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          {projects.length > 1 && (
            <select value={projectId || ''} onChange={(e) => setProjectId(e.target.value)} className="rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm focus:border-indigo-500 focus:ring-indigo-500">
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          )}
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)} className="rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm focus:border-indigo-500 focus:ring-indigo-500">
            <option value="7d">{t('analytics.last7Days')}</option>
            <option value="30d">{t('analytics.last30Days')}</option>
            <option value="90d">{t('analytics.last90Days')}</option>
          </select>
          <button onClick={handleExport} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
            <Download className="h-4 w-4 mr-2" />
            {t('common.export')}
          </button>
        </div>
      </div>

      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center space-x-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {!projectId && !loading && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <p className="text-sm text-yellow-700">No projects found. Create a project to see analytics.</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {loading ? (
          [...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
              <div className="flex items-center"><div className="h-8 w-8 bg-gray-200 rounded"></div><div className="ml-4 h-4 bg-gray-200 rounded w-20"></div></div>
              <div className="mt-3 h-8 bg-gray-200 rounded w-16"></div>
            </div>
          ))
        ) : (
          [
            { title: t('analytics.totalConversations'), value: analytics?.totalSessions?.toLocaleString() || '0', icon: MessageSquare, color: 'indigo' },
            { title: t('analytics.resolutionRate'), value: analytics ? `${analytics.totalMessages > 0 ? Math.round((analytics.totalMessages / Math.max(analytics.totalSessions, 1)) * 10) / 10 : 0} msg/session` : '--', icon: TrendingUp, color: 'green' },
            { title: t('analytics.avgResponseTime'), value: analytics?.avgResponseTime ? `${(analytics.avgResponseTime / 1000).toFixed(1)}s` : '--', icon: Clock, color: 'yellow' },
            { title: t('analytics.satisfactionScore'), value: analytics?.satisfactionScore ? analytics.satisfactionScore.toFixed(1) : '0.0', icon: Users, color: 'purple' }
          ].map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <stat.icon className={`h-8 w-8 text-${stat.color}-600`} />
                <h3 className="ml-4 text-sm font-medium text-gray-900">{stat.title}</h3>
              </div>
              <p className="mt-3 text-2xl font-semibold text-gray-900">{stat.value}</p>
            </div>
          ))
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">{t('analytics.conversationTrends')}</h3>
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center"><div className="h-3 w-3 bg-indigo-600 rounded mr-2"></div><span className="text-gray-600">{t('analytics.total')}</span></div>
              <div className="flex items-center"><div className="h-3 w-3 bg-green-600 rounded mr-2"></div><span className="text-gray-600">{t('analytics.resolved')}</span></div>
            </div>
          </div>
          {loading ? (
            <div className="animate-pulse space-y-4"><div className="h-4 bg-gray-200 rounded w-3/4"></div><div className="h-32 bg-gray-200 rounded"></div></div>
          ) : dailyStats.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">No conversation data for this period.</div>
          ) : (
            <div className="space-y-4">
              {dailyStats.slice(-14).map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="text-sm text-gray-600 w-20">{new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                  <div className="flex-1 mx-4">
                    <div className="flex space-x-1 h-6">
                      <div className="bg-indigo-600 rounded" style={{ width: `${(item.sessions / maxSessions) * 100}%` }}></div>
                      <div className="bg-green-600 rounded" style={{ width: `${(item.messages / Math.max(...dailyStats.map(d => d.messages), 1)) * 100}%` }}></div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-900 w-16 text-right">{item.sessions}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">{t('analytics.mostAskedQuestions')}</h3>
            <button className="text-indigo-600 hover:text-indigo-800 text-sm font-medium">{t('common.viewAll')}</button>
          </div>
          {loading ? (
            <div className="animate-pulse space-y-4">{[...Array(5)].map((_, i) => (<div key={i} className="flex items-center space-x-3"><div className="h-4 bg-gray-200 rounded flex-1"></div><div className="h-4 bg-gray-200 rounded w-8"></div></div>))}</div>
          ) : topics.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">No topic data available for this period.</div>
          ) : (
            <div className="space-y-4">
              {topics.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex-1"><p className="text-sm font-medium text-gray-900 truncate">{item.topic}</p></div>
                  <div className="flex items-center space-x-3 ml-4">
                    <span className="text-sm text-gray-500">{item.count}</span>
                    <div className={`h-2 w-2 rounded-full ${item.trend === 'up' ? 'bg-green-400' : item.trend === 'down' ? 'bg-red-400' : 'bg-gray-400'}`}></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">{t('analytics.responseTimeByHour')}</h3>
            <div className="text-sm text-gray-500">{t('analytics.averageSeconds')}</div>
          </div>
          {loading ? (
            <div className="animate-pulse"><div className="h-32 bg-gray-200 rounded"></div></div>
          ) : (
            <div className="space-y-4">
              {analytics?.avgResponseTime != null ? (
                <div className="text-center py-8">
                  <div className="text-3xl font-bold text-indigo-600 mb-2">{(analytics.avgResponseTime / 1000).toFixed(1)}s</div>
                  <p className="text-sm text-gray-500">Average response time across all sessions</p>
                  <div className="mt-4">
                    <div className="bg-gray-200 rounded-full h-3 max-w-xs mx-auto">
                      <div className="bg-indigo-600 h-3 rounded-full" style={{ width: `${Math.min((analytics.avgResponseTime / 5000) * 100, 100)}%` }}></div>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">{analytics.avgResponseTime}ms</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 text-sm">No response time data available.</div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">{t('analytics.customerSatisfaction')}</h3>
            <div className="text-sm text-gray-500">{t('analytics.outOf5')}</div>
          </div>
          {loading ? (
            <div className="animate-pulse"><div className="h-16 bg-gray-200 rounded mb-4"></div><div className="space-y-2"><div className="h-3 bg-gray-200 rounded"></div><div className="h-3 bg-gray-200 rounded w-3/4"></div></div></div>
          ) : (
            <div>
              <div className="text-center mb-6">
                <div className="text-4xl font-bold text-indigo-600 mb-2">{analytics?.satisfactionScore ? analytics.satisfactionScore.toFixed(1) : '0.0'}</div>
                <div className="flex justify-center space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <div key={star} className={`h-5 w-5 ${star <= Math.round(analytics?.satisfactionScore || 0) ? 'text-yellow-400' : 'text-gray-300'}`}>&#11088;</div>
                  ))}
                </div>
              </div>
              {analytics?.topCategories && analytics.topCategories.length > 0 ? (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">Top Categories</h4>
                  {analytics.topCategories.slice(0, 5).map((item) => {
                    const maxCount = Math.max(...analytics.topCategories.map(c => c.count), 1)
                    return (
                      <div key={item.category} className="flex items-center space-x-3">
                        <span className="text-sm text-gray-600 w-24 truncate">{item.category}</span>
                        <div className="flex-1 bg-gray-200 rounded-full h-2"><div className="bg-yellow-400 h-2 rounded-full" style={{ width: `${(item.count / maxCount) * 100}%` }}></div></div>
                        <span className="text-sm text-gray-500 w-8">{item.count}</span>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">No category data available.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
